import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import api from '../api/axios'

type BrandingMap = Record<string, string>

const FIELDS: Array<{
  key: string
  label: string
  help: string
  placeholder: string
  section: 'Company' | 'Email' | 'URLs'
}> = [
  {
    key: 'brand_parent_company',
    label: 'Parent Company (legal)',
    help: 'Shown in email footers as the owning entity (e.g. Pine State Technologies LLC).',
    placeholder: 'Pine State Technologies LLC',
    section: 'Company',
  },
  {
    key: 'brand_product_name',
    label: 'Product Name',
    help: 'Used in subjects and headings (e.g. CITS Time).',
    placeholder: 'CITS Time',
    section: 'Company',
  },
  {
    key: 'brand_affiliate_name',
    label: 'Affiliate Display Name',
    help: 'The brand presented as an affiliate (e.g. CruzIT Services).',
    placeholder: 'CruzIT Services',
    section: 'Company',
  },
  {
    key: 'brand_affiliate_url',
    label: 'Affiliate URL',
    help: 'Link attached to the affiliate display name.',
    placeholder: 'https://cruzitservices.com',
    section: 'Company',
  },
  {
    key: 'brand_support_email',
    label: 'Support Email',
    help: 'Shown to trial customers as a contact inbox.',
    placeholder: 'support@cruzitservices.com',
    section: 'Email',
  },
  {
    key: 'brand_from_email',
    label: 'System From Email',
    help: 'Display-only From address for branding. Actual envelope sender is the shared M365 mailbox.',
    placeholder: 'no-reply@cruzitapps.com',
    section: 'Email',
  },
  {
    key: 'brand_super_admin_portal_url',
    label: 'Super Admin Portal URL',
    help: 'Full URL used in trial notification emails.',
    placeholder: 'https://manage.cruzitapps.com',
    section: 'URLs',
  },
  {
    key: 'brand_tenant_portal_domain',
    label: 'Tenant Portal Domain',
    help: 'Root domain for all tenants; they are served at https://{slug}.{domain} (trial and active alike).',
    placeholder: 'cruzitapps.com',
    section: 'URLs',
  },
]

type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

const AUTOSAVE_DELAY_MS = 700

export default function BrandingSettings() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [values, setValues] = useState<BrandingMap>({})
  const savedRef = useRef<BrandingMap>({})
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data } = await api.get<{ keys: string[]; settings: BrandingMap }>('/super-admin/branding')
        if (!alive) return
        setValues(data.settings)
        savedRef.current = { ...data.settings }
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error).message
        setError(msg)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const scheduleSave = (next: BrandingMap) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setSaveState('pending')
    timerRef.current = setTimeout(() => persistDirty(next), AUTOSAVE_DELAY_MS)
  }

  const persistDirty = async (next: BrandingMap) => {
    const dirty: BrandingMap = {}
    for (const f of FIELDS) {
      const cur = next[f.key] ?? ''
      const prev = savedRef.current[f.key] ?? ''
      if (cur !== prev) dirty[f.key] = cur
    }
    if (Object.keys(dirty).length === 0) {
      setSaveState('idle')
      return
    }
    setSaveState('saving')
    setError(null)
    try {
      const { data } = await api.put<{ success: boolean; settings: BrandingMap }>(
        '/super-admin/branding',
        { settings: dirty }
      )
      savedRef.current = { ...data.settings }
      setSaveState('saved')
      setTimeout(() => {
        setSaveState((s) => (s === 'saved' ? 'idle' : s))
      }, 1800)
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error).message
      setError(msg)
      setSaveState('error')
    }
  }

  const handleChange = (key: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value }
      scheduleSave(next)
      return next
    })
  }

  const handleBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    persistDirty(values)
  }

  const sections = Array.from(new Set(FIELDS.map((f) => f.section)))

  const StatusPill = () => {
    if (saveState === 'saving') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">
          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> Saving…
        </span>
      )
    }
    if (saveState === 'pending') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full">
          Unsaved changes…
        </span>
      )
    }
    if (saveState === 'saved') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
          <CheckCircleIcon className="w-3.5 h-3.5" /> Saved
        </span>
      )
    }
    if (saveState === 'error') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
          <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Save failed
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 px-2.5 py-1">
        Changes save automatically
      </span>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
            aria-label="Back to settings"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <SparklesIcon className="w-7 h-7 text-blue-600" />
              Branding
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Copy and URLs used in trial signup, welcome, and notification emails.
            </p>
          </div>
          <StatusPill />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-500 text-sm">
            Loading branding settings…
          </div>
        ) : (
          sections.map((section) => (
            <div key={section} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{section}</h2>
              <div className="space-y-5">
                {FIELDS.filter((f) => f.section === section).map((field) => {
                  const raw = (values[field.key] ?? '').trim()
                  const previewHref = (() => {
                    if (!raw) return null
                    if (field.section !== 'URLs' && !field.key.endsWith('_url')) return null
                    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
                    return `https://${raw}`
                  })()
                  return (
                    <div key={field.key}>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor={field.key} className="block text-sm font-medium text-slate-800">
                          {field.label}
                        </label>
                        {previewHref && (
                          <a
                            href={previewHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                          >
                            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                            Open
                          </a>
                        )}
                      </div>
                      <input
                        id={field.key}
                        name={field.key}
                        type="text"
                        value={values[field.key] ?? ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        onBlur={handleBlur}
                        placeholder={field.placeholder}
                        autoComplete="off"
                        spellCheck={false}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-slate-500">{field.help}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
